import type { PropsWithChildren } from 'react';
import { createContext, useContext } from 'react';
import { useNextTickActivation } from './useNextTickActivation';
import { useTransitionActivation } from './useTransitionActivation';

export function createActivationManager<T>() {
  type Callback = () => void;

  const ActivationContext = createContext<{
    state: T;
    activate: (id: T, fn: Callback) => void;
  } | null>(null);

  const ActivationPendingContext = createContext<boolean>(false);

  type ActivationProviderProps = PropsWithChildren<{
    activeEntity: T;
    mode?: 'nextTick' | 'transition';
  }>;

  type ActivationProviderPropStrategy = Omit<
    ActivationProviderProps,
    'mode' | 'children'
  > & {
    children: (
      context: {
        state: T;
        activate: (id: T, fn: Callback) => void;
      },
      isPending: boolean
    ) => React.ReactNode;
  };

  function NextTickActivationProvider({
    activeEntity,
    children,
  }: ActivationProviderPropStrategy) {
    const { context, isPending } = useNextTickActivation<T>({
      activeEntity,
    });

    return children(context, isPending);
  }

  function TransitionActivationProvider({
    activeEntity,
    children,
  }: ActivationProviderPropStrategy) {
    const { context, isPending } = useTransitionActivation<T>({
      activeEntity,
    });

    return children(context, isPending);
  }

  function ActivationManager({
    activeEntity,
    children,
    mode = 'nextTick',
  }: ActivationProviderProps) {
    const Provider =
      mode === 'nextTick'
        ? NextTickActivationProvider
        : TransitionActivationProvider;

    return (
      <Provider activeEntity={activeEntity}>
        {(context, isPending) => (
          <ActivationPendingContext.Provider value={isPending}>
            <ActivationContext.Provider value={context}>
              {children}
            </ActivationContext.Provider>
          </ActivationPendingContext.Provider>
        )}
      </Provider>
    );
  }

  function useActivationIsPending() {
    const context = useContext(ActivationPendingContext);

    return context;
  }

  function useActivation() {
    const context = useContext(ActivationContext);
    if (!context) {
      throw new Error('useActivation must be used within a ActivationProvider');
    }

    return context;
  }

  return {
    ActivationManager,
    useActivation,
    useActivationIsPending,
  };
}
